import React, { Fragment, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Currency, Segment } from "../../types/common.types";
import { FormStateProps } from "./ModalProductUpdate";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { Box, Chip, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";
import { capitalizeWords } from "../../helpers/format";
import { darkFieldSx } from "./Name";

interface Props {
  optionId: number;
  updateOptions: (optionId: number) => void;
  currency: Currency;
}

const SaleOption = (props: Props): JSX.Element => {
  const { optionId, updateOptions, currency } = props;

  return (
    <Row className="mt-2">
      <Col sm={10}>
        <Row>
          <Form.Group as={Col} controlId="saleOptionLabel">
            <Form.Label>Label</Form.Label>
            <Form.Control type="text" placeholder="1 portion" />
          </Form.Group>
          <Form.Group as={Col} controlId="saleOptionPrice">
            <Form.Label>Price</Form.Label>
            <Form.Control type="text" placeholder={`${currency.prefix}0.99`} />
          </Form.Group>
        </Row>
      </Col>
      <Col sm={2}>
        <Form.Group as={Col} controlId="saleOptionClose">
          <Form.Label>Remove</Form.Label>
          <Button variant={"danger"} onClick={() => updateOptions(optionId)}>
            X
          </Button>
        </Form.Group>
      </Col>
    </Row>
  );
};

type SaleOptionObject = {
  element: JSX.Element;
  optionId: number;
};
interface PriceProp extends FormStateProps {
  currency: Currency;
  segments: Segment[];
}
const Price = (props: PriceProp) => {
  const { currency, segments, formFields, setFields } = props;
  const { prices } = formFields;
  const [elementId, setElementId] = useState(0);
  const user = useSelector((state: RootState) => state.Auth.user);
  const [saleOptions, setSaleOptions] = useState<SaleOptionObject[]>([
    {
      optionId: 0,
      element: <></>,
    },
  ]);

  const priceFor = useTranslation("price_for");

  const updateSaleOptions = (id: number) => {
    const filteredElements = saleOptions.filter(
      (element) => element.optionId !== id
    );
    setSaleOptions([...filteredElements]);
  };

  const optionAddHandler = () => {
    const optionsList = saleOptions;
    const optionId = elementId + 1;
    const newElement: SaleOptionObject = {
      element: (
        <SaleOption
          optionId={optionId}
          updateOptions={(id) => updateSaleOptions(id)}
          currency={currency}
        />
      ),
      optionId,
    };
    optionsList.push(newElement);
    setSaleOptions(optionsList);
    setElementId(optionId);
  };

  const isDefaultSegment = (segment: Segment) =>
    segment.isDefault || segment.name.toLowerCase() === "regular";

  const onPriceChange = (segment: Segment, value: string) => {
    const productPrices = { ...prices };
    const priceKey = segment.name;

    if (value === "") {
      delete productPrices[priceKey];
    } else {
      productPrices[priceKey] = Number(value);
    }

    setFields({ ...formFields, prices: productPrices });
  };

  return (
    <Box sx={{ display: "grid", gap: 1.25 }}>
      <Box>
        <Box sx={{ display: "grid", gap: 1 }}>
          {!!segments.length &&
            segments.map((segment, index) => {
              const isDefault = isDefaultSegment(segment);
              const multiplier = segment.priceMultiplier ?? 1;
              const isDisabled =
                !user.features?.includes("segment_map") && !isDefault;

              return (
                <Box key={`${segment.name}-${index}`}>
                  <TextField
                    id="standard-basic"
                    label={
                      isDefault
                        ? `${priceFor} ${capitalizeWords(segment.name)} ${
                            user.currency.prefix
                          }`
                        : `${capitalizeWords(segment.name)} override ${
                            user.currency.prefix
                          }`
                    }
                    placeholder={isDefault ? "Required" : "Use multiplier"}
                    size="small"
                    value={prices[segment.name] || ""}
                    onChange={(e) => onPriceChange(segment, e.target.value)}
                    type="number"
                    inputProps={{ min: 0.01, step: "0.01" }}
                    sx={darkFieldSx}
                    required={isDefault}
                    disabled={isDisabled}
                    helperText={
                      isDefault ? (
                        "Base price. Regular always uses multiplier 1x."
                      ) : (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          component="span"
                          sx={{ mt: 0.25 }}
                        >
                          <Typography
                            component="span"
                            sx={{ color: "#7a6560", fontSize: "11px" }}
                          >
                            Leave empty to use
                          </Typography>
                          <Chip
                            component="span"
                            size="small"
                            label={`${multiplier}x`}
                            sx={{
                              height: 18,
                              borderRadius: "999px",
                              bgcolor: "rgba(131, 60, 65, 0.18)",
                              color: "#d5a84c",
                              fontSize: "10px",
                            }}
                          />
                        </Stack>
                      )
                    }
                  />
                </Box>
              );
            })}
        </Box>
      </Box>

      <Box>
        <Form.Group controlId="priceOptionBtn">
          {/*<Form.Label>Sale Options</Form.Label>*/}
          <Form.Control
            type="button"
            value="Add price options"
            style={{
              cursor: "not-allowed",
              background: "#1f1615",
              color: "#7a6560",
              border: "1px solid rgba(131, 60, 65, 0.18)",
              borderRadius: "7px",
              fontSize: "13px",
              padding: "8px 12px",
            }}
            disabled
            onClick={() => optionAddHandler()}
          />
        </Form.Group>
      </Box>
      <Box>
        {saleOptions &&
          saleOptions.map((saleOption, index) => (
            <Fragment key={index}>{saleOption.element}</Fragment>
          ))}
      </Box>
    </Box>
  );
};

export default Price;
